import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import type { Build } from '@/api/schemas';
import { formatDateTime, shortSha, timeAgo } from '@/lib/format';
import { stagger } from '@/lib/motion';
import { useNow } from '@/lib/useNow';
import { ErrorState } from '@/components/ErrorState';
import { Icon } from '@/components/Icon';
import { Dot, EnvPill, PlatformIcon, StatusPill } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { STATUS_LABEL } from '@/features/builds/buildHelpers';
import { useCan } from '@/lib/permissions';
import { useRunBuild } from '@/shell/RunBuildContext';
import { GatedButton } from '@/components/GatedButton';
import { config } from '@/config';
import { isRunnable } from './setupModel';
import { StepCard, StepNote, code } from './StepCard';
import { RowsSkeleton, type StepProps } from './StepShared';
import { buildSummary, buildsEmptyHint, listView } from './stepNotesModel';
import { STEP_TITLE, isLocalApi, stepNumber } from './steps';
import type { Setup } from './useSetup';

/** One build: reference (links to its detail), status, environment, platform, branch and commit, when. */
function BuildRow({ build, projectSlug, now, index }: { build: Build; projectSlug: string; now: number; index: number }) {
  return (
    <li data-testid={`gs-build-${build.ref}`} data-status={build.status} style={stagger(index)} className="enter flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5 border-b px-3 py-2.5 last:border-b-0">
      <Link to="/projects/$slug/builds/$buildId" params={{ slug: projectSlug, buildId: build.id }} className="min-w-0 rounded-sm font-mono text-[13px] font-medium text-foreground underline-offset-4 hover:underline">
        {build.ref}
      </Link>
      <StatusPill status={build.status} label={STATUS_LABEL[build.status]} />
      <EnvPill env={build.environment} />
      <PlatformIcon platform={build.platform} size={16} />
      <span className="min-w-0 flex-1 truncate font-mono text-xs text-subtle" title={build.git.commitMessage ?? undefined}>
        {build.git.branch ?? '—'}
        {build.git.commitSha ? ` · ${shortSha(build.git.commitSha)}` : ''}
        {build.status !== 'succeeded' && build.statusReason ? ` · ${build.statusReason}` : ''}
      </span>
      <span className="shrink-0 font-mono text-xs text-subtle" title={formatDateTime(build.createdAt)}>
        {timeAgo(build.createdAt, now)}
      </span>
    </li>
  );
}

/** The selected profile can be started by Run build (Hangar's runner, or Bitrise's API). */
const canRun = (setup: Setup): boolean => Boolean(setup.profile && isRunnable(setup.profile, setup.bitrise));

/** The "Run build" button, for a runnable profile when the console can run builds. */
function runBuildButton(setup: Setup, current: boolean, onRun: ReturnType<typeof useRunBuild>['openRunBuild'], canBuild: ReturnType<typeof useCan>) {
  const { project, app, profile } = setup;
  if (!canRun(setup) || !project || !app || !profile || !setup.runBuild) return null;
  return (
    <GatedButton size="sm" variant={current ? 'default' : 'outline'} data-testid="gs-run-build" can={canBuild} onClick={() => onRun({ projectSlug: project.slug, appId: app.id, profileId: profile.id })}>
      <Icon name="bolt" size={16} />
      Run build
    </GatedButton>
  );
}

function BuildsFooter({ projectSlug, runButton }: { projectSlug: string; runButton: ReactNode }) {
  return (
    <>
      {runButton}
      <Button asChild size="sm" variant="outline">
        <Link to="/projects/$slug/builds" params={{ slug: projectSlug }} data-testid="gs-open-builds">
          Open Builds
          <Icon name="arrow_forward" size={16} />
        </Link>
      </Button>
    </>
  );
}

function LiveFootnote({ status }: { status: Setup['liveStatus'] }) {
  const live = status === 'connected';
  return (
    <span className="inline-flex items-center gap-1.5" data-testid="gs-builds-live" data-status={status}>
      <Dot tone={live ? 'succeeded' : 'subtle'} pulse={live} />
      {live ? 'Live: new builds appear here' : 'Connecting to live updates…'}
    </span>
  );
}

/** Platform operators only: on a local stack, provider webhooks need the tunnel (builds still finish without it). */
function TunnelNote() {
  return (
    <StepNote tone="muted" testId="gs-tunnel-note">
      Operator note: this Hangar runs on your machine, so EAS and Bitrise webhooks reach it only through the tunnel ({code('ngrok http 8080')} and {code('HANGAR_HOOKS_PUBLIC_URL')}). Without it, build status updates more slowly.
    </StepNote>
  );
}

/** Where the first build will appear, and how to start it. */
function EmptyBuilds({ runnable, runBuild }: { runnable: boolean; runBuild: boolean }) {
  return (
    <div data-testid="gs-builds-empty" className="flex flex-col items-center gap-2 rounded-lg border border-dashed px-4 py-8 text-center">
      <Icon name="hourglass_empty" size={24} className="text-subtle" />
      <p className="text-[13px] text-muted-foreground">{buildsEmptyHint(runnable, runBuild)}</p>
    </div>
  );
}

function BuildsBody({ setup, builds }: { setup: Setup; builds: Setup['builds'] }) {
  const now = useNow(15_000);
  const { project, app, buildList } = setup;
  if (!project || !app) return <StepNote tone="muted">Connect the app first (step {stepNumber('app')}).</StepNote>;
  const view = listView(builds, buildList);
  if (view === 'loading') return <RowsSkeleton rows={3} />;
  if (view === 'error') return <ErrorState compact error={builds.error} onRetry={() => void builds.refetch()} title="Couldn't load the builds" />;
  if (view === 'empty') return <EmptyBuilds runnable={canRun(setup)} runBuild={setup.runBuild} />;
  return (
    <ul aria-label={`Latest builds of ${app.name}`} className="flex min-w-0 flex-col rounded-lg border">
      {buildList!.map((b, i) => (
        <BuildRow key={b.id} build={b} projectSlug={project.slug} now={now} index={i} />
      ))}
    </ul>
  );
}

/** The latest build, or that there is none yet. */
const latestBuildNote = (list: Build[] | undefined): string => (list?.length ? `Latest ${list[0]!.ref} · ${STATUS_LABEL[list[0]!.status]}` : 'No build yet');

/**
 * Step 6 · First build: the latest builds of the app, live over SSE, or the place where the first one will appear. A
 * runnable profile (Hangar's runner for Expo, Bitrise's API) gets the console's Run build button (`features.runBuild`).
 * Builds from the CLI and CI return with docs/mvp-plan.md P17.
 */
export function FirstBuildStep({ setup, index }: StepProps) {
  const { project, buildList } = setup;
  const current = setup.progress.next === 'build';
  const { openRunBuild } = useRunBuild();
  const canBuild = useCan('builds:write', project?.id);
  const runButton = runBuildButton(setup, current, openRunBuild, canBuild);
  return (
    <StepCard
      state={setup.state('build')}
      current={current}
      index={index}
      title={STEP_TITLE.build}
      summary={buildSummary()}
      collapsedNote={latestBuildNote(buildList)}
      footnote={<LiveFootnote status={setup.liveStatus} />}
      footer={project ? <BuildsFooter projectSlug={project.slug} runButton={runButton} /> : null}
    >
      <BuildsBody setup={setup} builds={setup.builds} />
      {setup.operator && isLocalApi(config.apiUrl) ? <TunnelNote /> : null}
    </StepCard>
  );
}
